import { ModelType } from '@/enums/model-type.enum';
import { validateParameterValue } from '@/utils/nodeInputValidation';
import type { AiModelData } from '@/types/nodes/model';
import type { BaseNodeData, Schema } from '@/types/node';

const GPT_IMAGE_1_VERSION = 'GPT Image 1';

/** Mirrors core's `GPTModel`. */
const GPT_IMAGE_VERSIONS: ReadonlySet<string> = new Set(['GPT Image 2', 'GPT Image 1.5', GPT_IMAGE_1_VERSION]);

/** Mirrors core's `FREE_PLAN_BLOCKED_MODELS`, which is what actually blocks the run. */
const FREE_PLAN_BLOCKED_MODELS: ReadonlySet<string> = new Set([ModelType.GptImage25, ...GPT_IMAGE_VERSIONS]);

// These types serve several versions, so the variant identifies the run in place of the name.
const GPT_IMAGE_1_TYPES: ReadonlySet<string> = new Set([ModelType.GptImage1, ModelType.GptImage1Edit]);

// The schema FlowGraph's `gpt_image_1` migration adds to nodes saved without one.
const GPT_IMAGE_1_FALLBACK_MODEL_SCHEMA = {
  type: 'enum',
  default: 'GPT Image 2',
  options: ['GPT Image 2', 'GPT Image 1.5', GPT_IMAGE_1_VERSION],
  exposed: false,
} as unknown as Schema;

/** The version a run sends: evaluated as the graph does before execute, then resolved as core's `resolveGPTModel`. */
const resolveVariant = (name: string, { params, schema }: AiModelData): string => {
  const modelSchema = schema?.model ?? (name === ModelType.GptImage1 ? GPT_IMAGE_1_FALLBACK_MODEL_SCHEMA : undefined);
  const sent = modelSchema
    ? validateParameterValue(params?.model, modelSchema.default, modelSchema)?.value
    : params?.model;
  return typeof sent === 'string' && GPT_IMAGE_VERSIONS.has(sent) ? sent : GPT_IMAGE_1_VERSION;
};

export const isFreePlanBlockedModelNode = (data: BaseNodeData): boolean => {
  const modelData = data as AiModelData;
  const name = modelData.model?.name;
  if (!name) return false;
  if (!GPT_IMAGE_1_TYPES.has(name)) return FREE_PLAN_BLOCKED_MODELS.has(name);
  return FREE_PLAN_BLOCKED_MODELS.has(resolveVariant(name, modelData));
};
